import { FC, Fragment, useMemo, useRef, useState } from "react";
import Stack from "@mui/material/Stack";
import { Box, LinearProgress } from "@mui/material";
import type { GridApi } from "ag-grid-community";
import {
  ResourceFiltersWithAction,
  ResourceType,
} from "@/services/cloudchipr.api";
import { AutomationsFoundResourcesGridToolbar } from "@/components/pages/automations/common/components/found-resources/components/toolbar/AutomationsFoundResourcesGridToolbar";
import { useAppSelector } from "@/store/hooks";
import { gridResourceDataLoadingSelector } from "@/store/resources-grid/selectors/resource-data/gridResourceDataLoadingSelector";
import { ResourcesAgGridHiddenActions } from "@/components/pages/common/resources-ag-grid/utils/types/common";
import { ResourcesMasterGrid } from "@/components/pages/common/resources-ag-grid/ResourcesMasterGrid";
import { ResourcesGridResourcePayloadType } from "@/store/resources-grid/utils/types/common";
import { automationsFoundResourcesGridUpcomingActionsColumDef } from "@/components/pages/automations/common/components/found-resources/components/grid/automationsFoundResourcesGridUpcomingActionsColumDef";
import { AllTypeResourcesData } from "@/store/resources-grid/utils/types/resources";
import { generateAutomationFoundResourcesMasterGridId } from "@/components/pages/automations/common/components/found-resources/components/AutomationsFoundResourcesCard";

interface WorkflowProtectorFoundResourcesCardProps {
  accountIds: string[];
  singleGrid: boolean;
  gracePeriodEnabled: boolean;
  scheduleAction: string | null;
  resourceType: ResourceType;
  overrideGridData: AllTypeResourcesData[];
  action: ResourceFiltersWithAction["action"];
  snapshot: ResourceFiltersWithAction["snapshot"];
}

export const WorkflowProtectorFoundResourcesCard: FC<
  WorkflowProtectorFoundResourcesCardProps
> = ({
  accountIds,
  scheduleAction,
  gracePeriodEnabled,
  singleGrid,
  overrideGridData,
  resourceType,
  snapshot,
  action,
}) => {
  const masterGridId =
    generateAutomationFoundResourcesMasterGridId(resourceType);

  const sizeRef = useRef<HTMLDivElement | null>(null);

  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const loading = useAppSelector((state) =>
    gridResourceDataLoadingSelector(state, masterGridId),
  );

  const resourceFilters: ResourceFiltersWithAction = useMemo(() => {
    return {
      filter: {
        type: resourceType,
        filter_items: [],
        filter_groups: [],
        operators: [],
      },
      action,
      snapshot,
    };
  }, [resourceType, snapshot, action]);

  const dataToFetchResources: ResourcesGridResourcePayloadType = useMemo(() => {
    return { resourceFilters: resourceFilters.filter, accountIds: accountIds };
  }, [accountIds, resourceFilters]);

  if (!resourceType) {
    return null;
  }

  const additionalColumns = gracePeriodEnabled
    ? [automationsFoundResourcesGridUpcomingActionsColumDef]
    : undefined;

  return (
    <Stack key={resourceType}>
      <AutomationsFoundResourcesGridToolbar
        filters={resourceFilters}
        gridApi={gridApi}
        masterGridId={masterGridId}
        scheduleAction={scheduleAction}
        resourceType={resourceType}
      />

      <Box border={1} borderColor="grey.300" borderRadius={2} overflow="hidden">
        {loading && <LinearProgress />}

        <Fragment>
          <ResourcesMasterGrid
            notFullHeight
            gridApi={gridApi}
            setGridApi={setGridApi}
            preventDetailsDrawerOpener
            resourceType={resourceType}
            masterGridId={masterGridId}
            hiddenActions={hiddenActions}
            masterResourceType={resourceType}
            overrideGridData={overrideGridData}
            additionalColumns={additionalColumns}
            dataToFetchResources={dataToFetchResources}
            gridMaxHeight={singleGrid ? sizeRef.current?.clientHeight : 265}
          />

          <Box top={0} left={0} ref={sizeRef} position="fixed" height="89dvh" />
        </Fragment>
      </Box>
    </Stack>
  );
};

const hiddenActions: ResourcesAgGridHiddenActions = [
  "workflowCreate",
  "clean",
  "createTask",
  "filterOut",
];
